import angular from 'angular';

class PositionSearchFormController {
  constructor(
    $q,
    $timeout,
    $scope,
    $translate,
    DEBUG,
    LogService,
    APIService,
    PositionSearchService
  ) {
    "ngInject";
    this.$q = $q;
    this.$timeout = $timeout;
    this.$scope = $scope;
    this.$translate = $translate;
    this.DEBUG = DEBUG;
    this.LogService = LogService;
    this.APIService = APIService;
    this.PositionSearchService = PositionSearchService;
  }

  $onInit() {
    this._datePickersOpened = {};
    this.searchForm = {};

    // Update venue filters
    this.PositionSearchService.venueCustomSearchFields = this.staffUser.venues_custom_search_fields;

    this._restoreFilters();
  }

  get filterDefinitions() {
    return this.PositionSearchService && this.PositionSearchService.filterDefinitions || [];
  }

  get filteredPositionsCountByGroup() {
    return this.PositionSearchService && this.PositionSearchService.filteredPositionsCountByGroup || {};
  }

  get allPositionsCountByGroup() {
    return this.PositionSearchService && this.PositionSearchService.allPositionsCountByGroup || {};
  }

  set staffUser(staffUser) {
    this._staffUser = staffUser;
  }

  get staffUser() {
    return this._staffUser;
  }

  get allPositionsDebug() {
    return this.DEBUG && this.PositionSearchService && this.PositionSearchService.allPositions || [];
  }

  get filteredPositionsDebug() {
    return this.DEBUG && this.PositionSearchService && this.PositionSearchService.filteredPositions || [];
  }

  get anyFiltersActive() {
    return this.PositionSearchService && this.PositionSearchService.anyFiltersActive;
  }

  get datePickersOpened() {
    return this._datePickersOpened;
  }

  get showInvalidDateRangeError() {
    return this.searchForm.start_date
      && this.searchForm.end_date
      && this.searchForm.start_date > this.searchForm.end_date;
  }

  getFilterOptionsFor(filterName) {
    return this.PositionSearchService.availableOptionsForFilter(filterName);
  }

  getFilterOptionNamesFor(filterName) {
    if (!this._filterOptionNamesByFilter) {
      this._filterOptionNamesByFilter = {};
    }
    const filterOptions = this.getFilterOptionsFor(filterName);
    const filterOptionNames = filterOptions.map(option => option.name);

    if (!angular.equals(filterOptionNames, this._filterOptionNamesByFilter[filterName])) {
      this._filterOptionNamesByFilter[filterName] = filterOptionNames;
    }

    return this._filterOptionNamesByFilter[filterName];
  }

  getTranslatedFilterOptionNameFor(filterOption) {
    if (filterOption.translationKey) {
      return this.$translate(filterOption.translationKey);
    }
    return this.$q.when(filterOption.name);
  }

  selectFilterChanged(filterName) {
    const selectedOptionId = this.searchForm[filterName];
    this.LogService.debug(`PositionSearchFormController.filtersChanged(). searchForm:`, this.searchForm, "selectedOptionId:", selectedOptionId);

    // Always remove existing filter first (since we don't support multiple filters of the same type)
    this.PositionSearchService.removeFilterOption(filterName);

    // If we have an option, add the filter
    if (selectedOptionId) {
      this.PositionSearchService.addFilterOption(filterName, selectedOptionId);
    }
  }

  typeaheadFilterChanged(filterName) {
    const value = this.searchForm[filterName];
    this.LogService && this.LogService.debug(`PositionSearchForm typeaheadFilterChanged(${filterName}) value: '${value}'`, value);
    if (!value) {
      this.PositionSearchService.removeFilterOption(filterName);
    }
  }

  typeaheadFilterOnSelect(filterName, selectedOptionName) {
    this.LogService && this.LogService.debug(`PositionSearchForm.typeaheadFilterOnSelect() arguments:`, arguments);
    this.PositionSearchService.removeFilterOption(filterName);

    if (selectedOptionName) {
      const selectedOptionId = this.PositionSearchService.findOptionIdForOptionName(filterName, selectedOptionName);
      if (selectedOptionId) {
        this.PositionSearchService.addFilterOption(filterName, selectedOptionId);
      } else {
        this.LogService && this.LogService.warn(`typeaheadFilterOnSelect(${filterName}, ${selectedOptionName}) couldnt find matching option`);
      }
    }
  }

  dateChanged(filterName) {
    const dateValue = this.searchForm[filterName];
    this.LogService && this.LogService.debug(`PositionSearchForm.dateChanged(${filterName}). dateValue: ${dateValue}`, dateValue);
    if (dateValue) {
      this.PositionSearchService.addFilterValue(filterName, dateValue);

      if (filterName === 'start_date' && !this.searchForm.end_date) {
        // Automatically set end_date to same day
        this.searchForm.end_date = dateValue;
        this.PositionSearchService.addFilterValue('end_date', dateValue);
      }
    } else {
      this.PositionSearchService.removeFilterValue(filterName);
    }
  }

  openDatePicker(filterName) {
    this.datePickersOpened[filterName] = true;
  }

  applySearch() {
    this.PositionSearchService.applyFilters();
    this.dismiss();
  }

  resetSearch() {
    Object.keys(this.searchForm).forEach(filterName => {
      this.searchForm[filterName] = '';
    });
    this.PositionSearchService.resetFilters();
    this.PositionSearchService.applyFilters();
    this.dismiss();
  }

  dismiss() {
    if (this.onDismiss) {
      this.onDismiss();
    }
  }

  /**
   * Read active filters from PositionSearchService and update our searchForm accordingly, so that UI matches state
   */
  _restoreFilters() {
    return this.$timeout(() => {
      // Restore filters, if applicable
      const activeFilterOptionsByFilter = this.PositionSearchService.activeFilterOptionsByFilter;
      if (activeFilterOptionsByFilter) {
        Object.keys(activeFilterOptionsByFilter).forEach(filterName => {
          const filterOptionsById = activeFilterOptionsByFilter[filterName];
          const filter = this.PositionSearchService.getFilterDefinitionByName(filterName);
          Object.keys(filterOptionsById).forEach(filterOptionId => {
            const filterOption = filterOptionsById[filterOptionId];

            switch (filter.type) {
              case 'select':
              case 'typeahead':
                this.searchForm[filterName] = filterOption.id;
                break;
              default:
                break;
            }
          });
        });
      }

      const activeFilterValuesByFilter = this.PositionSearchService.activeFilterValuesByFilter;
      if (activeFilterValuesByFilter) {
        Object.keys(activeFilterValuesByFilter).forEach(filterName => {
          const value = activeFilterValuesByFilter[filterName];
          this.searchForm[filterName] = value;
        });
      }
    });
  }
}

export default PositionSearchFormController;
